import * as React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Header,
  ColumnResizeMode,
  ColumnResizeDirection,
  ColumnPinningPosition,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { cva, VariantProps } from 'class-variance-authority';
import { FilterIcon, FilterX } from 'lucide-react';

import { cn } from '../../../../lib/stylingUtils';
import { Button } from '../../buttons/Button';
import { Popover, PopoverTrigger, PopoverDialog } from '../../overlays/Popover';

import { HeaderIcon } from './Icons';

// Sort triangle component
export type SortTriangleProps = {
  direction: 'up' | 'down';

  className?: string;
};

const SortTriangle = ({ direction, className }: SortTriangleProps) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    className={cn('@SortTriangle', 'cursor-pointer', className)}
  >
    <path
      d={direction === 'up' ? 'M1.5 8L5 2.5L8.5 8' : 'M1.5 2L5 7.5L8.5 2'}
      fill="currentColor"
      rx="0.5"
    />
  </svg>
);

// Header specific styles
const headerVariants = cva('flex w-full items-center gap-2 font-medium', {
  variants: {
    sortable: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    sortable: false,
  },
});

// Header cell specific styles
const headerCellVariants = cva(
  [
    'border-r border-b border-foreground/20 bg-white p-2 align-middle text-foreground select-none last:border-r-0',
    'whitespace-nowrap [&:has([role=checkbox])]:pr-0',
  ],
  {
    variants: {
      dragging: {
        default: 'opacity-100',
        over: 'z-10 cursor-grabbing opacity-80',
        overlay: 'z-10 cursor-grabbing opacity-80',
      },
      resizing: {
        true: 'select-none',
        false: '',
      },
      sticky: {
        true: '',
        false: 'relative',
      },
      showShadow: {
        true: '',
        false: '',
      },
      showHShadow: {
        left: 'after:opacity-100',
        right: 'after:opacity-100',
        false: 'after:opacity-0',
      },

      // Pinning
      pinned: {
        false: '',
        left: [
          'sticky left-[var(--column-start)] z-10',
          'after:transition-opacity after:duration-200',
          'after:absolute after:top-0 after:right-[-5px] after:h-full after:w-[5px] after:bg-gradient-to-l',
          'after:pointer-events-none after:from-transparent after:to-neutral/10',
        ],
        right: [
          'sticky right-[var(--column-after)] z-10',
          'after:transition-opacity after:duration-200',
          'after:absolute after:top-0 after:left-[-5px] after:h-full after:w-[5px] after:bg-gradient-to-r',
          'after:pointer-events-none after:from-transparent after:to-neutral/10',
        ],
      },
    },
    compoundVariants: [
      {
        sticky: true,
        pinned: false,
        className: 'sticky z-1',
      },
      {
        sticky: true,
        pinned: 'left',
        className: 'sticky z-2',
      },
      {
        sticky: true,
        pinned: 'right',
        className: 'sticky z-2',
      },
    ],
    defaultVariants: {
      dragging: 'default',
      resizing: false,
      sticky: false,
      showShadow: false,
      pinned: false,
    },
  },
);

// Sort handle styles
const sortHandleWrapperVariants = cva('flex flex-col gap-0');

// Resize handle styles
const resizeHandleVariants = cva(
  'absolute top-0 h-full w-1 cursor-col-resize bg-primary/60 opacity-0 transition-opacity hover:opacity-100',
  {
    variants: {
      isResizing: {
        true: 'bg-primary/60 opacity-100',
        false: '',
      },
      direction: {
        ltr: 'right-0',
        rtl: 'left-0',
      },
    },
    defaultVariants: {
      isResizing: false,
      direction: 'ltr',
    },
  },
);

export type GroupRow<T> = {
  groupKey: React.Key;
  children: T[];
};

export type DataGridColumn<T> = {
  key: string;
  header: React.ReactNode;
  width?: number;
  pinned?: ColumnPinningPosition;
  resizable?: boolean;
  grow?: number;
  sortable?: boolean;
  icon?: HeaderIcon;
  cell: (props: { row: T; column: DataGridColumn<T> }) => React.ReactNode;
  groupCell?: (props: GroupRow<T>) => React.ReactNode;
  onClick?: (props: { row: T }) => void;
  onDoubleClick?: (props: { row: T }) => void;
  columnFilter?:
    | React.ReactNode
    | ((values: { close: () => void }) => React.ReactNode);
  isFilterActive?: boolean;
};

export interface TableHeaderProps<T> {
  header: Header<T, unknown>;
  className?: string;
  isOverlay?: boolean;
  columnResizeMode?: ColumnResizeMode;
  columnResizeDirection?: ColumnResizeDirection;
  columnSortDirection?: 'ascending' | 'descending';
  deltaOffset: number | null;
  onColumnResize?: (columnId: string, width: number) => void;
  onColumnSort?: (columnId: string) => void;
  sticky?: boolean;
  showShadow?: boolean;
  showHShadow?: VariantProps<typeof headerCellVariants>['showHShadow'];
  columnFilter?:
    | React.ReactNode
    | ((values: { close: () => void }) => React.ReactNode);
  isFilterActive?: boolean;
}

export const TableHeader = <T,>({
  header,
  className,
  isOverlay,
  columnResizeMode,
  columnResizeDirection,
  columnSortDirection,
  deltaOffset,
  onColumnResize,
  onColumnSort,
  sticky,
  showShadow,
  showHShadow,
  columnFilter,
  isFilterActive,
}: TableHeaderProps<T>) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  const handleResize = React.useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const resizeHandler = header.getResizeHandler();
      resizeHandler(e);

      if (onColumnResize && header.column.getSize() !== undefined) {
        onColumnResize(header.column.id, header.column.getSize());
      }
    },
    [header, onColumnResize],
  );

  const pinningStyles: React.CSSProperties = {
    width: header.column.getSize(),
    transform: CSS.Translate.toString(transform),
    '--column-after': `${header.column.getAfter('right')}px`,
    '--column-start': `${header.column.getStart('left') + header.column.getIndex()}px`,
  } as React.CSSProperties;

  return (
    <th
      colSpan={header.colSpan}
      ref={setNodeRef}
      className={cn(
        '@TableHeader',
        headerCellVariants({
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
          sticky: sticky,
          className,
          showShadow,
          showHShadow,
          pinned: header.column.getIsPinned(),
        }),
      )}
      style={pinningStyles}
    >
      <div className="pointer-events-none absolute inset-0 bg-foreground/7" />
      {header.isPlaceholder ? null : (
        <div
          className={headerVariants({ sortable: header.column.getCanSort() })}
          {...attributes}
          {...listeners}
        >
          <div className="grow">
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
          {columnFilter && (
            <PopoverTrigger>
              <Button
                variant={isFilterActive ? 'solid' : 'ghost'}
                size="icon"
                icon={isFilterActive ? <FilterX /> : <FilterIcon />}
                className="size-6"
                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              />
              <Popover placement="bottom">
                <PopoverDialog>
                  {({ close }: { close: () => void }) =>
                    typeof columnFilter === 'function'
                      ? columnFilter({ close })
                      : columnFilter
                  }
                </PopoverDialog>
              </Popover>
            </PopoverTrigger>
          )}
          {header.column.getCanSort() && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onColumnSort?.(header.column.id)}
              className={sortHandleWrapperVariants()}
            >
              <SortTriangle
                direction="up"
                className={
                  columnSortDirection === 'ascending'
                    ? 'text-neutral'
                    : 'text-neutral/20'
                }
              />
              <SortTriangle
                direction="down"
                className={
                  columnSortDirection === 'descending'
                    ? 'text-neutral'
                    : 'text-neutral/20'
                }
              />
            </div>
          )}
        </div>
      )}

      {header.column.getCanResize() && (
        <div
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={handleResize}
          onTouchStart={handleResize}
          className={resizeHandleVariants({
            isResizing: header.column.getIsResizing(),
            direction: columnResizeDirection,
          })}
          style={{
            transform:
              columnResizeMode === 'onEnd' && header.column.getIsResizing()
                ? `translateX(${(columnResizeDirection === 'rtl' ? -1 : 1) * (deltaOffset ?? 0)}px)`
                : undefined,
          }}
        />
      )}
    </th>
  );
};
